import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../firebaseConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TableSortLabel,
} from '@mui/material';

const EditUser = ({ setMessage }) => {
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [newUser, setNewUser] = useState({
    unitNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    cellNumber: '',
    certLevel: '',
    password: '',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false); // For editing user
  const [orderBy, setOrderBy] = useState('unitNumber');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'users'));
        const usersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setMessage('Error fetching users. Please try again.');
      }
    };

    fetchUsers();
  }, [setMessage]);

  const handleEditClick = user => {
    setEditingUserId(user.id);
    setEditedUser(user);
    setEditDialogOpen(true); // Open the edit dialog
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditedUser({ ...editedUser, [name]: value });
  };

  const handleNewUserChange = e => {
    const { name, value } = e.target;

    if (name === 'unitNumber') {
      const email = `${value}@hatzalahbaltimore.us`;
      setNewUser({ ...newUser, [name]: value, email });
    } else {
      setNewUser({ ...newUser, [name]: value });
    }
  };

  const handleSaveClick = async () => {
    try {
      const updatedUser = {
        ...editedUser,
        displayName: `${editedUser.unitNumber} ${editedUser.firstName} ${editedUser.lastName}`,
      };

      const userDocRef = doc(db, 'users', editingUserId);
      await updateDoc(userDocRef, updatedUser);
      setUsers(users.map(user => (user.id === editingUserId ? updatedUser : user)));
      setEditDialogOpen(false); // Close edit dialog
      setMessage('User updated successfully.');
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage('Error updating user. Please try again.');
    }
  };

  const handleAddUserClick = async () => {
    const auth = getAuth();
    try {
      if (!newUser.unitNumber || !newUser.firstName || !newUser.password) {
        setMessage('Please fill out all required fields.');
        return;
      }
      const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
      const user = userCredential.user;

      const newUserWithDisplayName = {
        unitNumber: newUser.unitNumber,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        role: newUser.role,
        cellNumber: newUser.cellNumber,
        certLevel: newUser.certLevel,
        displayName: `${newUser.unitNumber} ${newUser.firstName} ${newUser.lastName}`,
      };

      await addDoc(collection(db, 'users'), { uid: user.uid, ...newUserWithDisplayName });
      setUsers([...users, { id: user.uid, ...newUserWithDisplayName }]);

      setNewUser({
        unitNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        cellNumber: '',
        certLevel: '',
        password: '',
      });
      setMessage('New user added successfully.');
      setDialogOpen(false);
    } catch (error) {
      console.error('Error adding new user:', error);
      setMessage('Error adding new user. Please try again.');
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);

    const sortedUsers = [...users].sort((a, b) => {
      const aValue = a[column] ?? ''; // Use empty string as fallback if the property is undefined
      const bValue = b[column] ?? '';
      return isAsc
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setUsers(sortedUsers);
  };

  return (
    <div>
      <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)}>
        Add New User
      </Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate autoComplete="off">
            <TextField name="unitNumber" label="Unit Number" value={newUser.unitNumber} onChange={handleNewUserChange} fullWidth margin="normal" />
            <TextField name="firstName" label="First Name" value={newUser.firstName} onChange={handleNewUserChange} fullWidth margin="normal" />
            <TextField name="lastName" label="Last Name" value={newUser.lastName} onChange={handleNewUserChange} fullWidth margin="normal" />
            <TextField name="email" label="Email" value={newUser.email} fullWidth margin="normal" disabled />
            <Select name="role" value={newUser.role} onChange={handleNewUserChange} fullWidth margin="normal" displayEmpty>
              <MenuItem value="" disabled>
                Select Role
              </MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Responder">Responder</MenuItem>
              <MenuItem value="Ops">Ops</MenuItem>
            </Select>
            <TextField name="cellNumber" label="Cell Number" value={newUser.cellNumber} onChange={handleNewUserChange} fullWidth margin="normal" />
            <Select name="certLevel" value={newUser.certLevel} onChange={handleNewUserChange} fullWidth margin="normal" displayEmpty>
              <MenuItem value="" disabled>
                Select Certification Level
              </MenuItem>
              <MenuItem value="BLS">BLS</MenuItem>
              <MenuItem value="ALS">ALS</MenuItem>
              <MenuItem value="Physician">Physician</MenuItem>
            </Select>
            <TextField name="password" label="Password" type="password" value={newUser.password} onChange={handleNewUserChange} fullWidth margin="normal" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddUserClick} color="primary">
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate autoComplete="off">
            <TextField name="unitNumber" label="Unit Number" value={editedUser.unitNumber} onChange={handleInputChange} fullWidth margin="normal" />
            <TextField name="firstName" label="First Name" value={editedUser.firstName} onChange={handleInputChange} fullWidth margin="normal" />
            <TextField name="lastName" label="Last Name" value={editedUser.lastName} onChange={handleInputChange} fullWidth margin="normal" />
            <TextField name="email" label="Email" value={editedUser.email} fullWidth margin="normal" disabled />
            <Select name="role" value={editedUser.role} onChange={handleInputChange} fullWidth margin="normal" displayEmpty>
              <MenuItem value="" disabled>
                Select Role
              </MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Responder">Responder</MenuItem>
              <MenuItem value="Ops">Ops</MenuItem>
            </Select>
            <TextField name="cellNumber" label="Cell Number" value={editedUser.cellNumber} onChange={handleInputChange} fullWidth margin="normal" />
            <Select name="certLevel" value={editedUser.certLevel} onChange={handleInputChange} fullWidth margin="normal" displayEmpty>
              <MenuItem value="" disabled>
                Select Certification Level
              </MenuItem>
              <MenuItem value="BLS">BLS</MenuItem>
              <MenuItem value="ALS">ALS</MenuItem>
              <MenuItem value="Physician">Physician</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveClick} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table of Users */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={orderBy === 'unitNumber'} direction={order} onClick={() => handleSort('unitNumber')}>Unit Number</TableSortLabel>
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Provider Level</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.unitNumber}</TableCell>
                <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.cellNumber}</TableCell>
                <TableCell>{user.certLevel}</TableCell>
                <TableCell>
                  <Button onClick={() => handleEditClick(user)} color="primary">
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default EditUser;
